import { useMemo } from "react";
import { useSearchParams, type ReadonlyURLSearchParams } from "next/navigation";
import { buttonLogger } from "../utils/logger";
import { clientEnv } from "../../lib/env";
import isBrowser from "../utils/isBrowser";

export interface UrlParameters {
  appId: string | null;
  configId: string | null;
  callbackUrl: string | null;
  cliSessionId: string | null;
  state: string | null;
}

export interface ConfigSources {
  bodyConfigId?: string | null;
  urlConfigId?: string | null;
  envConfigId?: string | null;
  urlAppId?: string | null;
  envAppId?: string | null;
}

export const validateCallbackUrl = (url: string | null): string | null => {
  if (!url) return null;
  const invalidDomains = [
    "your-app.vercel.app",
    "example.com",
    "localhost.example",
    "test.example.com",
  ];
  try {
    const parsedUrl = new URL(url);
    const isZapierOAuth =
      (parsedUrl.hostname === "zapier.com" ||
        parsedUrl.hostname === "zapier-staging.com") &&
      parsedUrl.pathname.startsWith("/dashboard/auth/oauth/return/");
    const isLocalhost =
      parsedUrl.hostname === "localhost" ||
      parsedUrl.hostname.endsWith(".local");
    if (
      !isZapierOAuth &&
      !isLocalhost &&
      invalidDomains.some((d) => parsedUrl.hostname.includes(d))
    ) {
      buttonLogger.warn("invalid_callback_url_rejected", {
        hostname: parsedUrl.hostname,
        reason: "placeholder_domain",
      });
      return null;
    }
    if (
      clientEnv.NEXT_PUBLIC_VERCEL_ENV === "production" &&
      !isLocalhost &&
      parsedUrl.protocol !== "https:"
    ) {
      buttonLogger.warn("invalid_callback_url_rejected", {
        protocol: parsedUrl.protocol,
        reason: "insecure_protocol",
      });
      return null;
    }
    return url;
  } catch (error) {
    buttonLogger.warn("invalid_callback_url_rejected", {
      error: error instanceof Error ? error.message : String(error),
      reason: "invalid_url_format",
    });
    return null;
  }
};

export const getAppId = (
  searchParams: URLSearchParams | ReadonlyURLSearchParams,
  sources: ConfigSources = {},
): string | null => {
  const { urlAppId, envAppId } = sources;
  const urlApp =
    urlAppId ||
    searchParams.get("app_id") ||
    searchParams.get("FACEBOOK_APP_ID");
  if (urlApp) {
    return urlApp;
  }
  const envApp = envAppId || clientEnv.NEXT_PUBLIC_FACEBOOK_APP_ID;
  if (envApp) {
    return envApp;
  }
  buttonLogger.error("missing_app_id", {
    source: "VALIDATION",
    message: "app_id is required but not found in URL or environment",
  });
  return null;
};

export const getConfigId = (
  searchParams: URLSearchParams | ReadonlyURLSearchParams,
  sources: ConfigSources = {},
): string | null => {
  const { bodyConfigId, urlConfigId, envConfigId } = sources;
  if (bodyConfigId) {
    buttonLogger.info("using_body_config_id", {
      source: "POST_BODY",
      configIdLength: bodyConfigId.length,
    });
    return bodyConfigId;
  }
  const urlConfig =
    urlConfigId ||
    searchParams.get("config_id") ||
    searchParams.get("FACEBOOK_CONFIG_ID");
  if (urlConfig) {
    return urlConfig;
  }
  const envConfig = envConfigId || clientEnv.NEXT_PUBLIC_FACEBOOK_CONFIG_ID;
  if (envConfig) {
    return envConfig;
  }
  buttonLogger.error("missing_config_id", {
    source: "PRODUCTION_VALIDATION",
    message: "config_id is required but not found in URL or environment",
  });
  return null;
};

export const useUrlParameters = (
  externalSources: ConfigSources = {},
): UrlParameters => {
  const searchParams = useSearchParams();
  return useMemo(() => {
    if (!searchParams || !isBrowser()) {
      return {
        appId: externalSources.urlAppId || null,
        configId: externalSources.bodyConfigId || null,
        callbackUrl: null,
        cliSessionId: null,
        state: null,
      };
    }
    const appId = getAppId(searchParams, externalSources);
    const configId = getConfigId(searchParams, externalSources);
    // STRICT: use only redirect_uri provided by CLI. Avoid fallback logic to reduce mismatch risk.
    const rawCallbackUrl = searchParams.get("redirect_uri");
    const cliSessionId = searchParams.get("cli_session");
    const state = searchParams.get("state");
    const callbackUrl = validateCallbackUrl(rawCallbackUrl);
    return { appId, configId, callbackUrl, cliSessionId, state };
  }, [
    searchParams,
    externalSources.bodyConfigId,
    externalSources.urlConfigId,
    externalSources.envConfigId,
    externalSources.urlAppId,
    externalSources.envAppId,
  ]);
};
